import { ProductResultsOrderDirection, ProductsResultsColumn } from '@clubhub/ignite/models/products/product-results-configuration';

/**
 * Mirrors the API contract in `MSS.Infrastructure/FilterPreferences/Models/FilterPreferencesModels.cs`.
 *
 * Throughout, `null` means "use default" - the owner has expressed no preference and the system default applies.
 * That is distinct from an empty selection, which is an explicit choice.
 */

export enum FilterPreferenceMode {
  SystemDefault = 1,
  OwnerPreferences = 2,
}

/**
 * One selected option of one Product search filter. Deliberately the same shape as
 * `MiProductsFiltersViewModel`, which is what `ProductsFilterService.applySavedFilters` consumes, so a stored
 * preference can later be replayed through the code path that already applies saved searches.
 */
export interface ProductFilterPreferenceItem {
  filterGroup: string;
  filterName: string;
  isChecked: boolean;
  isIncluded: boolean;
  isExcluded: boolean;
  numericValue?: number | null;
  fromValue?: number | null;
  toValue?: number | null;
  /**
   * Nested selections beneath this filter - currently the packagers under Tailored products. Sent as the
   * packager names, which is what the runtime sub-filter matches on.
   */
  checkedSubFilters: string[];
}

/**
 * Whether the owner has deliberately configured one tool.
 *
 * Only a broker sends this. `false` means "I have no preferences of my own for this tool, follow my firm" and
 * stores no row; `true` means the rest of the section is the broker's own choice, including an all-defaults
 * section, which for them reads as "the system defaults, not my firm's". Those two are indistinguishable by
 * value alone, which is why the flag exists.
 *
 * `null` is what the distribution group admin screen sends, and keeps the original behaviour.
 */
interface ConfigurableFilterPreferences {
  isConfigured: boolean | null;
  /**
   * Which individual slots within the section the broker set, by their display title. Anything not named here is
   * still following the firm, whatever value the section happens to carry for it.
   *
   * Null is what the admin screen sends, and means "every slot in this section is mine".
   */
  configuredFilters: string[] | null;
}

export interface ProductFilterPreferences extends ConfigurableFilterPreferences {
  mode: FilterPreferenceMode;
  filters: ProductFilterPreferenceItem[];
  sortOn: ProductsResultsColumn | null;
  sortDirection: ProductResultsOrderDirection | null;
  isRateTypeOverridable: boolean | null;
  isInitialProductPeriodOverridable: boolean | null;
  isProductFeaturesOverridable: boolean | null;
  isTailoredProductsOverridable: boolean | null;
  isCriteriaOverridable: boolean | null;
  isAffordabilityMetOverridable: boolean | null;
  isSortOnOverridable: boolean | null;
  isSortDirectionOverridable: boolean | null;
}

export interface AffordabilityFilterPreferences extends ConfigurableFilterPreferences {
  mode: FilterPreferenceMode;
  affordabilityMet: string | null;
  criteriaOutcomes: string | null;
  propertyOutcomes: string | null;
  productMatched: string | null;
  isAffordabilityMetOverridable: boolean | null;
  isCriteriaOutcomesOverridable: boolean | null;
  isPropertyOutcomesOverridable: boolean | null;
  isProductMatchedOverridable: boolean | null;
}

export interface CriteriaFilterPreferences extends ConfigurableFilterPreferences {
  mode: FilterPreferenceMode;
  meetsCriteria: string[] | null;
  isMeetsCriteriaOverridable: boolean | null;
}

export interface FilterPreferences {
  productSearch: ProductFilterPreferences;
  affordabilitySearch: AffordabilityFilterPreferences;
  criteriaSearch: CriteriaFilterPreferences;
}

/**
 * What a broker's My account page loads: their own stored preferences and, underneath, their firm's.
 *
 * Two layers rather than one merged result, because the page has to distinguish a setting the broker chose from
 * one they are inheriting, and show the firm's locked settings as read-only.
 */
export interface BrokerFilterPreferences {
  /** Null when the broker has never saved. Null values within it mean "follow the firm". */
  own: FilterPreferences | null;
  /** Null when the broker is in no distribution group, or the group has Filter preferences switched off. */
  firmDefaults: FilterPreferences | null;
}

/** Local wrapper mirroring `DistributionGroupProductResultsConfigurationLocalModel`, so unsaved edits are tracked. */
export interface FilterPreferencesLocalModel {
  isUpdated: boolean;
  model: FilterPreferences;
}

const defaultProductFilterPreferences = {
  isConfigured: null,
  configuredFilters: null,
  mode: FilterPreferenceMode.SystemDefault,
  filters: [],
  sortOn: null,
  sortDirection: null,
  isRateTypeOverridable: null,
  isInitialProductPeriodOverridable: null,
  isProductFeaturesOverridable: null,
  isTailoredProductsOverridable: null,
  isCriteriaOverridable: null,
  isAffordabilityMetOverridable: null,
  isSortOnOverridable: null,
  isSortDirectionOverridable: null,
} satisfies ProductFilterPreferences;

const defaultAffordabilityFilterPreferences = {
  isConfigured: null,
  configuredFilters: null,
  mode: FilterPreferenceMode.SystemDefault,
  affordabilityMet: null,
  criteriaOutcomes: null,
  propertyOutcomes: null,
  productMatched: null,
  isAffordabilityMetOverridable: null,
  isCriteriaOutcomesOverridable: null,
  isPropertyOutcomesOverridable: null,
  isProductMatchedOverridable: null,
} satisfies AffordabilityFilterPreferences;

const defaultCriteriaFilterPreferences = {
  isConfigured: null,
  configuredFilters: null,
  mode: FilterPreferenceMode.SystemDefault,
  meetsCriteria: null,
  isMeetsCriteriaOverridable: null,
} satisfies CriteriaFilterPreferences;

export const defaultFilterPreferences = {
  productSearch: defaultProductFilterPreferences,
  affordabilitySearch: defaultAffordabilityFilterPreferences,
  criteriaSearch: defaultCriteriaFilterPreferences,
} satisfies FilterPreferences;
